import React, { useEffect } from 'react';
import './MobileTopBar.css';
import rdvWideImg from '../../assets/rdv-logoW.png';

/**
 * Slim top bar for the mobile shell (AuthenticatedLayout ≤768px). Brand only —
 * the menu trigger lives in MobileBottomBar.
 *
 * Scrolls away with the content. The bar has to be position:fixed (it is a
 * sibling of .main-content--mobile, and every screen scrolls its OWN inner
 * container — the shell has no scrollport), so "part of the scroll" is
 * reproduced by translating it up by exactly the container's scrollTop until
 * it clears its own height, then leaving it there. Scroll down 30px, the bar
 * moves up 30px; scroll back, it comes back the same 30px.
 *
 * 1:1 and NO transition, both deliberate. The previous version mapped scroll
 * onto a 0..1 "hide" over 96px and eased it over 0.18s, which meant the bar
 * travelled ~1.5x faster than the finger, and a quick flick back to the top
 * crossed the whole range in one coarse scroll event — the transition then had
 * nothing to interpolate and snapped open. Moving in lockstep with the scroll
 * has no timing of its own, so there is nothing left to snap.
 *
 * The strip the bar occupies is reserved INSIDE each scrollport (padding-top on
 * .projects-content), not outside it. That is what makes this gap-free: the
 * content already extends under the bar, so the bar leaving reveals content
 * rather than a void, and the content itself never has to move to compensate.
 */
const MobileTopBar = ({ title }) => {
  useEffect(() => {
    let raf = 0;

    const onScroll = (e) => {
      const t = e.target;
      // Elements only (the shell never scrolls the document), and only
      // VERTICAL scrollers — the breadcrumb pill scrolls sideways and its
      // scrollTop of 0 would wrongly slam the bar back mid-gesture.
      if (!t || !t.scrollHeight || !(t.scrollHeight - t.clientHeight > 24)) {
        return;
      }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const bar = document.querySelector('.mobile-top-bar');
        if (!bar) return;
        // Cap at the bar's own height: past that it is fully gone and any
        // further scrolling must not keep pushing it (which would drag it up
        // behind the status bar and make the return trip start late).
        const shift = Math.min(Math.max(t.scrollTop, 0), bar.offsetHeight);
        document.documentElement.style.setProperty('--tb-shift', `${shift}px`);
      });
    };

    // Capture phase: scroll events don't bubble, but they do capture through
    // ancestors, so one document listener hears every screen's inner scroller.
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      cancelAnimationFrame(raf);
      // Leaving the mobile shell (rotation/desktop) must not strand a shifted
      // bar on a layout that never scrolls it back.
      document.documentElement.style.removeProperty('--tb-shift');
    };
  }, []);

  return (
    <header className='mobile-top-bar'>
      <div className='mobile-top-bar__brand'>
        <img src={rdvWideImg} alt='RDV' className='mobile-top-bar__logo' />
        {title ? <span className='mobile-top-bar__title'>{title}</span> : null}
      </div>
    </header>
  );
};

export default MobileTopBar;
